import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import {
  Settings,
  ArrowLeft,
  Shield,
  Globe,
  Database,
  Mail,
  Key,
  Palette,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Server,
  Lock,
  Sparkles,
  Copy,
  Trash2,
  ExternalLink,
  Download,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';


const PlatformSettings = () => {
  const [settings, setSettings] = useState({
    platform_name: 'Social Impact Investment Platform',
    platform_description: 'Comprehensive platform for social impact project management and investment tracking',
    admin_email: 'admin@platform.com',
    max_users: 1000,
    max_projects: 500,
    max_file_size_mb: 50,
    allowed_file_types: ['pdf', 'doc', 'docx', 'xlsx', 'ppt', 'pptx', 'jpg', 'png'],
    enable_notifications: true,
    enable_analytics: true,
    enable_user_registration: false,
    maintenance_mode: false,
    theme_color: '#2563eb',
    logo_url: '',
    backup_frequency: 'daily',
    session_timeout_hours: 24
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Market-Test Demo Pack state
  const [demoSeeding, setDemoSeeding] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [demoError, setDemoError] = useState(null);

  useEffect(() => {
    fetchPlatformSettings();
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      // For now, we'll use default settings since we don't have a backend endpoint yet
      // In a real implementation, you would fetch from /api/admin/platform-settings
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load platform settings');
      setLoading(false);
    }
  };

  const savePlatformSettings = async () => {
    try {
      setSaving(true);
      
      // Simulate saving settings (in real implementation, would call /api/admin/platform-settings)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Platform settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save platform settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleArraySettingChange = (key, value) => {
    const arrayValue = (value || '').split(',').map(item => item.trim()).filter(item => item);
    setSettings(prev => ({
      ...prev,
      [key]: arrayValue
    }));
  };

  const confirmCriticalAction = (action, message) => {
    setConfirmAction({ action, message });
    setShowConfirmDialog(true);
  };

  const executeConfirmedAction = () => {
    if (confirmAction) {
      confirmAction.action();
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const restartPlatform = () => {
    confirmCriticalAction(
      () => {
        toast.success('Platform restart initiated (simulated)');
      },
      'This will restart all platform services and briefly interrupt user access. Continue?'
    );
  };

  const enableMaintenanceMode = () => {
    confirmCriticalAction(
      () => {
        handleSettingChange('maintenance_mode', true);
        toast.warning('Maintenance mode enabled - users will see maintenance page');
      },
      'This will put the platform in maintenance mode and prevent user access. Continue?'
    );
  };

  // ── Market-Test Demo Pack ─────────────────────────────────────────
  const seedMarketTestDemo = async () => {
    setDemoError(null);
    setDemoSeeding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/demo/market-test/seed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoResult(res.data);
      toast.success('Market-Test demo pack seeded — INMEKO + Social Economy Hub ready');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Seed failed';
      setDemoError(msg);
      toast.error(`Demo seed failed: ${msg}`);
    } finally {
      setDemoSeeding(false);
    }
  };

  const provisionInmekoTeamUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/demo/market-test/team-user`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tu = res.data?.team_user || {};
      toast.success(
        `Team account ready — username "${tu.username}" / password "${tu.password}" (${tu.created ? 'created' : 'password reset'})`,
        { duration: 10000 }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Provision failed';
      toast.error(`Team-user provision failed: ${msg}`);
    }
  };

  const resetInmekoPassword = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/demo/market-test/reset-inmeko-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const u = res.data?.user || {};
      toast.success(
        `inmeko2026 password reset — username "${u.username}" / password "${u.password}"`,
        { duration: 10000 }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Reset failed';
      toast.error(`inmeko2026 password reset failed: ${msg}`);
    }
  };

  const repairInmeko = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/demo/market-test/repair`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const actions = res.data?.actions || [];
      toast.success(
        `INMEKO repair complete — ${actions.length} step(s): ${actions.slice(0, 2).join('; ')}${actions.length > 2 ? '…' : ''}`,
        { duration: 12000 }
      );
      // Also print the full audit trail to the console for the admin.
      // eslint-disable-next-line no-console
      console.log('[INMEKO repair]', res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Repair failed';
      toast.error(`INMEKO repair failed: ${msg}`);
    }
  };

  // ─── KOISPE New Horizons Demo Pack ────────────────────────────────
  const [koispeBusy, setKoispeBusy] = useState(false);
  const [koispeResult, setKoispeResult] = useState(null);

  const seedKoispe = async () => {
    setKoispeBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/koispe-demo/seed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKoispeResult(res.data);
      toast.success(
        `KOISPE demo pack ready — ${res.data.credentials.username} / ${res.data.credentials.password}`,
        { duration: 12000 }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Seed failed';
      toast.error(`KOISPE seed failed: ${msg}`);
    } finally {
      setKoispeBusy(false);
    }
  };

  const wipeKoispe = () => {
    confirmCriticalAction(
      'This deletes the KOISPE investor profile, the New Horizons Inclusion Fund, its 3 segments and 3 investee Social Actors. The koispe2026 user remains. Proceed?',
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${BACKEND_URL}/api/admin/koispe-demo/wipe`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setKoispeResult(null);
          toast.success('KOISPE demo data wiped.');
        } catch (err) {
          const msg = err?.response?.data?.detail || err.message || 'Wipe failed';
          toast.error(`KOISPE wipe failed: ${msg}`);
        }
      }
    );
  };

  const resetKoispePassword = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/koispe-demo/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const u = res.data?.user || {};
      toast.success(
        `koispe2026 password reset — username "${u.username}" / password "${u.password}"`,
        { duration: 10000 }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Reset failed';
      toast.error(`koispe2026 password reset failed: ${msg}`);
    }
  };

  const downloadKoispeReport = async (lang = 'en') => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/koispe-demo/report.docx?lang=${lang}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `koispe_market_testing_report_${lang}_${new Date().toISOString().slice(0,10)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`KOISPE Market Test Report (${lang.toUpperCase()}) downloaded`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      toast.error(`KOISPE report (${lang.toUpperCase()}) download failed: ${msg}`);
    }
  };

  const [koispeElBusy, setKoispeElBusy] = useState(false);
  const [koispeElStatus, setKoispeElStatus] = useState(null);

  const syncKoispeEl = async () => {
    setKoispeElBusy(true);
    setKoispeElStatus({ el_report_status: 'in_progress' });
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/koispe-demo/sync-el`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Poll every 3 s up to 2 minutes.
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const res = await axios.get(
          `${BACKEND_URL}/api/admin/koispe-demo/sync-el/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setKoispeElStatus(res.data);
        if (res.data.el_report_status === 'completed') {
          toast.success('KOISPE EL translation completed and cached.');
          break;
        }
        if (res.data.el_report_status === 'failed') {
          toast.error(`KOISPE EL translation failed: ${res.data.el_report_error || 'unknown'}`);
          break;
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Sync failed';
      toast.error(`KOISPE EL sync failed: ${msg}`);
    } finally {
      setKoispeElBusy(false);
    }
  };

  const downloadKoispeBundle = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/koispe-demo/bundle.zip`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `koispe_market_test_bundle_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('KOISPE Annex bundle downloaded');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      toast.error(`KOISPE bundle download failed: ${msg}`);
    }
  };

  const purgeMarketTestDemo = () => {
    confirmCriticalAction(
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${BACKEND_URL}/api/admin/demo/market-test`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setDemoResult(null);
          setDemoError(null);
          toast.success('Market-Test demo data wiped (user account preserved)');
        } catch (err) {
          toast.error('Failed to wipe demo data');
        }
      },
      'This will permanently delete the INMEKO social-actor profile, the Social Economy Hub project (with all KPIs/expenses/payments), the "Social Economy Tourist Routes" blended plan with its 3 segments, and the 3 INMEKO capacity-building enrollments. The user account `inmeko2026` is preserved. Continue?'
    );
  };

  const copyToClipboard = (text, label = 'Value') => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error('Copy failed — please copy manually')
    );
  };

  const downloadInmekoMarketTestingReport = async (lang = 'en') => {
    try {
      const SA_ID = 'b38e2cbe-ba1c-47a5-a395-ffd0d68f168a'; // INMEKO
      const token = localStorage.getItem('token');
      toast.info(lang === 'el'
        ? 'Παραγωγή αναφοράς Market Testing (EL)…'
        : 'Generating Market Testing Report (EN)…');
      const res = await axios.get(
        `${BACKEND_URL}/api/market-testing/actor/${SA_ID}/report?lang=${lang}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `Market_Testing_Report_INMEKO_${stamp}${lang === 'el' ? '_EL' : '_EN'}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'el' ? 'Η αναφορά κατέβηκε ✓' : 'Report downloaded ✓');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'download failed';
      toast.error(`Report download failed: ${msg}`);
    }
  };

  const [translateSyncBusy, setTranslateSyncBusy] = useState(false);
  const [translateSyncStatus, setTranslateSyncStatus] = useState(null);

  const syncInmekoElTranslations = async () => {
    const SA_ID = 'b38e2cbe-ba1c-47a5-a395-ffd0d68f168a';
    const token = localStorage.getItem('token');
    setTranslateSyncBusy(true);
    setTranslateSyncStatus({ status: 'starting' });
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/demo/translate-evaluator-data/${SA_ID}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('LLM translation job started — polling for completion…');
      // Poll up to 4 minutes
      const start = Date.now();
      const poll = async () => {
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/demo/translate-status/${SA_ID}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTranslateSyncStatus(r.data);
        if (r.data.status === 'completed') {
          const s = r.data.summary || {};
          toast.success(
            `EL translations synced ✓ (${s.module_evaluations || 0} module evals · ${s.ci_items || 0} CI items · ${s.investments || 0} investments)`
          );
          setTranslateSyncBusy(false);
          return;
        }
        if (r.data.status === 'failed') {
          toast.error(`Translation failed: ${r.data.error || 'unknown error'}`);
          setTranslateSyncBusy(false);
          return;
        }
        if (Date.now() - start > 240000) {
          toast.warning('Still running after 4 min — check status manually.');
          setTranslateSyncBusy(false);
          return;
        }
        setTimeout(poll, 6000);
      };
      setTimeout(poll, 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'sync failed';
      toast.error(`Translation sync failed: ${msg}`);
      setTranslateSyncBusy(false);
    }
  };

  const downloadInmekoMarketTestingBundle = async () => {
    try {
      const SA_ID = 'b38e2cbe-ba1c-47a5-a395-ffd0d68f168a';
      const token = localStorage.getItem('token');
      toast.info('Generating the full Market Testing bundle (EN + EL reports + all annex PDFs)…');
      const res = await axios.get(
        `${BACKEND_URL}/api/market-testing/actor/${SA_ID}/full-bundle.zip`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `Market_Testing_Bundle_INMEKO_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bundle downloaded ✓ (includes EN + EL reports + every annex PDF)');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'download failed';
      toast.error(`Bundle download failed: ${msg}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Link>
          <div className="border-l border-gray-300 h-8"></div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Settings className="h-7 w-7 text-blue-600" />
              <span>Platform Settings</span>
            </h2>
            <p className="text-gray-600 mt-1">
              Configure platform-wide settings and preferences
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={restartPlatform}
            className="text-orange-700 border-orange-300 hover:bg-orange-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Restart Platform
          </Button>
          <Button onClick={savePlatformSettings} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Maintenance Mode Alert */}
      {settings.maintenance_mode && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-700" />
              <div>
                <h4 className="font-medium text-orange-900">Maintenance Mode Active</h4>
                <p className="text-sm text-orange-700">
                  The platform is currently in maintenance mode. Users cannot access the system.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => handleSettingChange('maintenance_mode', false)}
                className="ml-auto"
              >
                Disable Maintenance
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-blue-600" />
              <span>General Settings</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Platform Name</Label>
              <Input
                value={settings.platform_name}
                onChange={(e) => handleSettingChange('platform_name', e.target.value)}
                placeholder="Platform name"
              />
            </div>
            
            <div>
              <Label>Platform Description</Label>
              <Textarea
                value={settings.platform_description}
                onChange={(e) => handleSettingChange('platform_description', e.target.value)}
                placeholder="Brief description of the platform"
                className="min-h-[80px]"
              />
            </div>
            
            <div>
              <Label>Administrator Email</Label>
              <Input
                type="email"
                value={settings.admin_email}
                onChange={(e) => handleSettingChange('admin_email', e.target.value)}
                placeholder="admin@example.com"
              />
            </div>
            
            <div>
              <Label>Theme Color</Label>
              <div className="flex items-center space-x-2">
                <Input
                  type="color"
                  value={settings.theme_color}
                  onChange={(e) => handleSettingChange('theme_color', e.target.value)}
                  className="w-16 h-10"
                />
                <Input
                  value={settings.theme_color}
                  onChange={(e) => handleSettingChange('theme_color', e.target.value)}
                  placeholder="#2563eb"
                  className="flex-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Database className="h-5 w-5 text-green-700" />
              <span>System Limits</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Maximum Users</Label>
              <Input
                type="number"
                value={settings.max_users}
                onChange={(e) => handleSettingChange('max_users', parseInt(e.target.value))}
                min="1"
              />
            </div>
            
            <div>
              <Label>Maximum Projects</Label>
              <Input
                type="number"
                value={settings.max_projects}
                onChange={(e) => handleSettingChange('max_projects', parseInt(e.target.value))}
                min="1"
              />
            </div>
            
            <div>
              <Label>Maximum File Size (MB)</Label>
              <Input
                type="number"
                value={settings.max_file_size_mb}
                onChange={(e) => handleSettingChange('max_file_size_mb', parseInt(e.target.value))}
                min="1"
              />
            </div>
            
            <div>
              <Label>Session Timeout (Hours)</Label>
              <Input
                type="number"
                value={settings.session_timeout_hours}
                onChange={(e) => handleSettingChange('session_timeout_hours', parseInt(e.target.value))}
                min="1"
                max="168"
              />
            </div>
          </CardContent>
        </Card>

        {/* File Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Server className="h-5 w-5 text-purple-600" />
              <span>File Management</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Allowed File Types</Label>
              <Input
                value={settings.allowed_file_types.join(', ')}
                onChange={(e) => handleArraySettingChange('allowed_file_types', e.target.value)}
                placeholder="pdf, doc, docx, xlsx, jpg, png"
              />
              <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
            </div>
            
            <div>
              <Label>Backup Frequency</Label>
              <Select 
                value={settings.backup_frequency} 
                onValueChange={(value) => handleSettingChange('backup_frequency', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Logo URL</Label>
              <Input
                value={settings.logo_url}
                onChange={(e) => handleSettingChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </CardContent>
        </Card>

        {/* Security & Access */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Lock className="h-5 w-5 text-red-600" />
              <span>Security & Access</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable User Registration</Label>
                <p className="text-sm text-gray-500">Allow new users to register themselves</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enable_user_registration}
                onChange={(e) => handleSettingChange('enable_user_registration', e.target.checked)}
                className="h-4 w-4"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Notifications</Label>
                <p className="text-sm text-gray-500">Send system notifications to users</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enable_notifications}
                onChange={(e) => handleSettingChange('enable_notifications', e.target.checked)}
                className="h-4 w-4"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Analytics</Label>
                <p className="text-sm text-gray-500">Track platform usage and performance</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enable_analytics}
                onChange={(e) => handleSettingChange('enable_analytics', e.target.checked)}
                className="h-4 w-4"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Maintenance Mode</Label>
                <p className="text-sm text-gray-500">Temporarily disable user access</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={enableMaintenanceMode}
                className="text-orange-700 border-orange-300"
              >
                Enable Maintenance
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Market-Test Demo Pack */}
      <Card data-testid="market-test-demo-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            <span>Market-Test Demo Pack</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900">
              <div className="flex items-start space-x-2">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">One-click market-test scenario.</p>
                  <p>
                    Rebuilds the <strong>INMEKO</strong> social actor profile,
                    the <strong>Social Economy Hub</strong> project (in
                    <em> executing</em> state with KPI actuals, deliverables
                    delivered, payments made, etc.), the
                    {' '}<strong>Social Economy Tourist Routes</strong> blended
                    plan (3 segments) and enrolls INMEKO in
                    <strong> all 3 Capacity-Building packages</strong>.
                  </p>
                  <p className="text-xs text-violet-700">
                    Idempotent — re-running deletes the previous demo rows and
                    rotates the password for <code className="font-mono">inmeko2026</code>.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="seed-market-test-demo-btn"
                onClick={seedMarketTestDemo}
                disabled={demoSeeding}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {demoSeeding ? 'Seeding…' : 'Seed market-test demo pack'}
              </Button>
              <Button
                data-testid="purge-market-test-demo-btn"
                onClick={purgeMarketTestDemo}
                disabled={demoSeeding}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Wipe demo data
              </Button>
              <Button
                data-testid="provision-inmeko-team-user-btn"
                onClick={provisionInmekoTeamUser}
                variant="outline"
                className="border-violet-300 text-violet-800 hover:bg-violet-50"
                title="Creates (or resets) the inmeko_team Social Actor account that shares INMEKO's data with inmeko2026. Safe to re-run."
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Provision inmeko_team account
              </Button>
              <Button
                data-testid="reset-inmeko-password-btn"
                onClick={resetInmekoPassword}
                variant="outline"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                title="Resets the inmeko2026 password back to the documented stable value InmekoEvaluator2026!. Does NOT touch any other data."
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Reset inmeko2026 password
              </Button>
              <Button
                data-testid="repair-inmeko-btn"
                onClick={repairInmeko}
                variant="outline"
                className="border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-semibold"
                title="Non-destructive repair: ensures the inmeko2026 user, INMEKO social-actor profile, Social Economy Hub project and inmeko_team account all exist and are correctly linked. Never wipes existing data."
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Repair INMEKO data
              </Button>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-emerald-800">
                INMEKO Market Testing Report (.docx)
              </p>
              <p className="mb-3 text-xs text-emerald-900">
                Audit-grade evidence of pilot/market-testing usage (TRL-7/8)
                pulled live from the Social Actor Registry, Social Investment
                Registry, Blended Investments, Capacity Building, Participatory
                Design, Investment Evaluation and Continuous Improvement modules.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  data-testid="download-inmeko-mt-report-en-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadInmekoMarketTestingReport('en')}
                  className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download · English (.docx)
                </Button>
                <Button
                  data-testid="download-inmeko-mt-report-el-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadInmekoMarketTestingReport('el')}
                  className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Λήψη · Ελληνικά (.docx)
                </Button>
                <Button
                  data-testid="download-inmeko-mt-bundle-btn"
                  variant="default"
                  size="sm"
                  onClick={downloadInmekoMarketTestingBundle}
                  className="bg-emerald-700 hover:bg-emerald-800"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Full bundle (.zip) — EN + EL + all annexes
                </Button>
                <Button
                  data-testid="sync-inmeko-el-translations-btn"
                  variant="outline"
                  size="sm"
                  onClick={syncInmekoElTranslations}
                  disabled={translateSyncBusy}
                  className="border-violet-400 text-violet-800 hover:bg-violet-50"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${translateSyncBusy ? 'animate-spin' : ''}`} />
                  {translateSyncBusy ? 'Syncing EL via LLM…' : 'Sync EL translations (LLM)'}
                </Button>
              </div>
              {translateSyncStatus && (
                <div
                  data-testid="translation-sync-status"
                  className="mt-2 rounded-md border border-violet-200 bg-violet-50/60 p-2 text-xs text-violet-900"
                >
                  Status: <strong>{translateSyncStatus.status}</strong>
                  {translateSyncStatus.summary && translateSyncStatus.status === 'completed' && (
                    <span>
                      {' '}— {translateSyncStatus.summary.module_evaluations || 0} module evals ·{' '}
                      {translateSyncStatus.summary.ci_items || 0} CI items ·{' '}
                      {translateSyncStatus.summary.investments || 0} investments
                    </span>
                  )}
                </div>
              )}
              <p className="mt-2 text-xs text-emerald-700">
                The bundle includes both EN/EL reports plus annex PDFs:
                Social Actor profile, Investment-Evaluation bundles per investment,
                Project reports (comprehensive / financial / KPIs / beneficiaries),
                Accessibility VPAT, RBAC matrix and a machine-readable JSON
                evidence dump.
              </p>
            </div>

            {demoError && (
              <div
                data-testid="market-test-demo-error"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
              >
                <strong>Seed failed.</strong> {demoError}
              </div>
            )}

            {demoResult && (
              <div
                data-testid="market-test-demo-result"
                className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900"
              >
                <div className="mb-3 flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-emerald-700" />
                  <p className="font-semibold">
                    Demo pack ready — {new Date(demoResult.seeded_at).toLocaleString()}
                  </p>
                </div>

                <div className="mb-3 rounded-md border border-emerald-200 bg-white p-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-emerald-700">
                    Login credentials (share with evaluators)
                  </p>
                  <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded bg-emerald-50 px-2 py-1">
                      <span>
                        <span className="text-emerald-700">Username:</span>{' '}
                        <code className="font-mono">{demoResult.user.username}</code>
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(demoResult.user.username, 'Username')}
                        aria-label="Copy username"
                        data-testid="copy-demo-username-btn"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between rounded bg-emerald-50 px-2 py-1">
                      <span>
                        <span className="text-emerald-700">Password:</span>{' '}
                        <code data-testid="demo-password-value" className="font-mono">{demoResult.user.password}</code>
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(demoResult.user.password, 'Password')}
                        aria-label="Copy password"
                        data-testid="copy-demo-password-btn"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>

                <ul className="space-y-1 text-xs">
                  <li>
                    <strong>Social Actor:</strong>{' '}
                    {demoResult.social_actor.organization_name} —{' '}
                    <span className="font-mono">{demoResult.social_actor.status}</span>{' '}
                    · cert: {demoResult.social_actor.certification_level}
                  </li>
                  <li>
                    <strong>Project:</strong>{' '}
                    {demoResult.project.title} ({demoResult.project.phase}) —{' '}
                    {demoResult.project.kpis} KPIs ·{' '}
                    {demoResult.project.deliverables} deliverables ·{' '}
                    {demoResult.project.expenses} expenses /{' '}
                    {demoResult.project.payments} payments
                  </li>
                  <li>
                    <strong>Blended investment:</strong>{' '}
                    {demoResult.blended_investment.title} —{' '}
                    {demoResult.blended_investment.segments} segments
                  </li>
                  <li>
                    <strong>Capacity-building:</strong>{' '}
                    {demoResult.capacity_building.enrollments} package
                    {demoResult.capacity_building.enrollments === 1 ? '' : 's'} enrolled
                  </li>
                </ul>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={`/project-details/${demoResult.project.id}`}
                    className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                    data-testid="open-demo-project-link"
                  >
                    Open project <ExternalLink className="ml-1 h-3 w-3" />
                  </Link>
                  <Link
                    to="/blended-investments"
                    className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                    data-testid="open-demo-bi-link"
                  >
                    Open blended-investment plan <ExternalLink className="ml-1 h-3 w-3" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KOISPE New Horizons Demo Pack */}
      <Card className="border-indigo-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Banknote className="h-5 w-5 text-indigo-700" />
            <span>KOISPE New Horizons — Investor Demo Pack</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Provisions a complete Social <strong>Investor</strong> showcase parallel to INMEKO:
              the <em>koispe2026</em> investor account, the KOISPE New Horizons
              investor profile, the <em>New Horizons Inclusion Fund</em>
              (€750k, 3 segments, 4 investees incl. INMEKO) and an
              investor-flavoured Market Test Report with capital-deployment,
              exit-strategy and ESG/SROI scorecards.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="seed-koispe-btn"
                onClick={seedKoispe}
                disabled={koispeBusy}
                className="bg-indigo-700 hover:bg-indigo-800"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {koispeBusy ? 'Seeding KOISPE…' : 'Seed KOISPE Demo Pack'}
              </Button>
              <Button
                data-testid="wipe-koispe-btn"
                onClick={wipeKoispe}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Wipe KOISPE demo data
              </Button>
              <Button
                data-testid="reset-koispe-password-btn"
                onClick={resetKoispePassword}
                variant="outline"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                title="Resets the koispe2026 password back to the documented stable value KoispeInvestor2026!. Data-safe."
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Reset koispe2026 password
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="download-koispe-report-en-btn"
                onClick={() => downloadKoispeReport('en')}
                variant="outline"
                size="sm"
                className="border-indigo-400 text-indigo-800 hover:bg-indigo-50"
              >
                <Download className="mr-2 h-4 w-4" />
                Download · English (.docx)
              </Button>
              <Button
                data-testid="download-koispe-report-el-btn"
                onClick={() => downloadKoispeReport('el')}
                variant="outline"
                size="sm"
                className="border-indigo-400 text-indigo-800 hover:bg-indigo-50"
              >
                <Download className="mr-2 h-4 w-4" />
                Λήψη · Ελληνικά (.docx)
              </Button>
              <Button
                data-testid="sync-koispe-el-btn"
                onClick={syncKoispeEl}
                disabled={koispeElBusy}
                variant="outline"
                size="sm"
                className="border-violet-400 text-violet-800 hover:bg-violet-50"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${koispeElBusy ? 'animate-spin' : ''}`} />
                {koispeElBusy ? 'Syncing EL via LLM…' : 'Sync EL translation (LLM)'}
              </Button>
              <Button
                data-testid="download-koispe-bundle-btn"
                onClick={downloadKoispeBundle}
                variant="default"
                size="sm"
                className="bg-indigo-700 hover:bg-indigo-800"
              >
                <Download className="mr-2 h-4 w-4" />
                Full bundle (.zip) — EN + EL + Cover + JSON
              </Button>
            </div>

            {koispeElStatus && (
              <div
                data-testid="koispe-el-status"
                className="rounded-md border border-violet-200 bg-violet-50/60 p-2 text-xs text-violet-900"
              >
                EL translation status: <strong>{koispeElStatus.el_report_status || '—'}</strong>
                {koispeElStatus.el_report_completed_at && (
                  <span> · completed {new Date(koispeElStatus.el_report_completed_at).toLocaleString()}</span>
                )}
                {koispeElStatus.el_report_error && (
                  <span className="text-red-700"> · {koispeElStatus.el_report_error}</span>
                )}
              </div>
            )}

            <p className="text-xs text-indigo-700">
              Stable credentials: <code className="font-mono">koispe2026</code> /{' '}
              <code className="font-mono">KoispeInvestor2026!</code> · role{' '}
              <code className="font-mono">investor</code>. The Annex bundle
              includes a one-page audit cover PDF with QR code, the EN Word
              report, a machine-readable portfolio-evidence JSON dump and a
              MANIFEST.
            </p>

            {koispeResult && (
              <div
                data-testid="koispe-demo-result"
                className="rounded-lg border border-indigo-300 bg-indigo-50 p-4 text-sm text-indigo-900"
              >
                <div className="mb-2 flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-indigo-700" />
                  <p className="font-semibold">
                    KOISPE demo pack ready — investor {koispeResult.credentials.organization_name}
                  </p>
                </div>
                <ul className="ml-4 list-disc text-[13px]">
                  <li>Fund: <strong>New Horizons Inclusion Fund</strong> · €750k · 3 segments</li>
                  <li>Investees ({koispeResult.seeded.investee_count}): INMEKO, NEXUS Wellbeing Society, GAEA Green Workshops, HARMONIA Music Inclusion Lab</li>
                  <li>Login: <code className="font-mono">{koispeResult.credentials.username}</code> / <code className="font-mono">{koispeResult.credentials.password}</code></li>
                </ul>
                <div className="mt-3 flex gap-2">
                  <Link
                    to="/blended-investments"
                    className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-800 hover:bg-indigo-100"
                    data-testid="open-koispe-bi-link"
                  >
                    Open New Horizons Inclusion Fund <ExternalLink className="ml-1 h-3 w-3" />
                  </Link>
                  <Link
                    to="/social-investors"
                    className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-800 hover:bg-indigo-100"
                    data-testid="open-koispe-investor-link"
                  >
                    Open KOISPE investor profile <ExternalLink className="ml-1 h-3 w-3" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>System Status</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <h4 className="font-medium">Database</h4>
              <p className="text-sm text-gray-500">Connected</p>
            </div>
            
            <div className="text-center p-4 border rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <h4 className="font-medium">Backend API</h4>
              <p className="text-sm text-gray-500">Running</p>
            </div>
            
            <div className="text-center p-4 border rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <h4 className="font-medium">File Storage</h4>
              <p className="text-sm text-gray-500">Available</p>
            </div>
            
            <div className="text-center p-4 border rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <h4 className="font-medium">Email Service</h4>
              <p className="text-sm text-gray-500">Configured</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-700" />
              <span>Confirm Action</span>
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.message}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={executeConfirmedAction} className="bg-orange-700 hover:bg-orange-800">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformSettings;